import {
  Bot,
  Check,
  ChevronDown,
  FileWarning,
  Folder,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,
  Trash2,
} from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MarkdownContent } from "@/components/chat/MarkdownContent";
import { ShareMenu } from "@/components/notebook/ShareMenu";
import { useDebouncedAutosave } from "@/hooks/useDebouncedAutosave";
import type { Note } from "@/hooks/useNotebook";
import { cn } from "@/lib/utils";
import { NOTEBOOK_MAX_NOTE_BYTES } from "@domain/image-compression";
import { parentFolderOf } from "@domain/notebook-folders";
import { notebookNoteTooLarge } from "@domain/user-errors";

// Tiptap + ProseMirror is ~100KB — only pulled in when a note is opened for
// editing, never on the read path or other routes. lazy() keeps it in a
// route-split chunk (spec §12.4); the editor uses Tailwind `prose` classes and
// imports no CSS of its own, so there is nothing to eager-load in main.tsx.
const NotebookEditor = lazy(() =>
  import("@/components/notebook/NotebookEditor").then((m) => ({ default: m.NotebookEditor })),
);

/** Save intent — a persisted note body, plus the title from which the note's
 *  path is (re)derived. A title change is a rename-as-move (handled upstream). */
export interface NotebookSaveArgs {
  title: string;
  content: string;
}

interface NotebookDocViewProps {
  /** The selected note's path (identity). `null` never reaches here — the
   *  route render-gates on a non-null selectedPath. */
  path: string;
  /** The fetched note body + frontmatter, or null while loading / not-found. */
  note: Note | null;
  /** Vault mtime from the list row — drives the ShareMenu staleness hint. */
  updatedAt?: string | null;
  isLoading: boolean;
  /** True when the read resolved to NOT_FOUND (deleted/renamed out from under). */
  notFound: boolean;
  /** Read failure that is NOT not-found (transport/oversize) — display copy. */
  loadError?: string | null;
  /** Save/delete failure display copy — rendered inline under the toolbar. */
  actionError?: string | null;
  /** Human title, derived from the path — the editable title field seeds from it. */
  title: string;
  /** Open directly in edit mode (used right after "New note"). */
  startEditing?: boolean;
  /** True while a COMMIT is in flight (the rename-capable save behind Done). */
  isSaving?: boolean;
  /** Resolves true on success — edit mode closes ONLY then; a failed save keeps
   *  the draft open (the parent surfaces the failure via `actionError`). */
  onSave: (args: NotebookSaveArgs) => Promise<boolean> | boolean;
  /** BODY-ONLY write to the note's current path, fired on a typing pause.
   *  MUST NOT rename, move the selection, or refresh the list (§4.13). */
  onAutosave?: (args: NotebookSaveArgs) => Promise<boolean>;
  onDelete: () => void;
  /** True while the note list is collapsed (full-screen reading). */
  isListHidden?: boolean;
  /** Collapse/restore the note list. Omit to hide the control entirely. */
  onToggleList?: () => void;
  /** All folder paths — powers the "move to folder" picker in the toolbar. */
  folders?: string[];
  /** Move this note into a folder ("" = root). Omit to hide the picker. */
  onMove?: (folder: string) => void;
  onBack?: () => void;
}

/** Reading + editing pane for one notebook document. Owns per-note loading and
 *  not-found states (§4.3); read/save go through the parent's path-keyed hook. */
export function NotebookDocView({
  path,
  note,
  updatedAt = null,
  isLoading,
  notFound,
  loadError = null,
  actionError = null,
  title: initialTitle,
  startEditing = false,
  isSaving = false,
  onSave,
  onAutosave,
  isListHidden = false,
  onToggleList,
  onDelete,
  folders = [],
  onMove,
  onBack,
}: NotebookDocViewProps) {
  const [isEditing, setIsEditing] = useState(startEditing);
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(note?.content ?? "");
  const [justSaved, setJustSaved] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  /** Pre-save size-guard copy (D6) — caught client-side, never a 413 trip. */
  const [sizeError, setSizeError] = useState<string | null>(null);
  /** Latest title, read (not closed over) by the autosave callback. Keeping it
   *  out of that callback's deps stops a title keystroke from re-arming the
   *  BODY debounce — the two fields pause independently. */
  const titleRef = useRef(title);
  titleRef.current = title;

  const currentFolder = parentFolderOf(path);
  const moveTargets = useMemo(() => {
    const set = new Set(folders);
    set.delete(currentFolder);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [folders, currentFolder]);

  // Re-sync the draft only when switching to a *different* note (keyed on the
  // caller's `key={selectedPath}` remount) or when the fetched body first
  // lands. Read mode always renders the live note.content.
  useEffect(() => {
    setTitle(initialTitle);
    setContent(note?.content ?? "");
    setIsEditing(startEditing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, note?.content]);

  // Clear the "Saved" flash + stale size-guard copy once a fresh edit starts.
  useEffect(() => {
    if (justSaved) setJustSaved(false);
    if (sizeError) setSizeError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, title]);

  /** Byte-accurate (UTF-8) pre-write size guard (D6) — catches an over-cap body
   *  inline instead of round-tripping the server's 413. */
  function isOverCap(body: string): boolean {
    return new TextEncoder().encode(body).length > NOTEBOOK_MAX_NOTE_BYTES;
  }

  // Autosave the BODY on a typing pause. The title is passed only so a draft's
  // first write can name its file; a title CHANGE never renames here — that is
  // the commit's job (see `notebook.tsx:handleAutosave`).
  const autosaveBody = useCallback(
    async (body: string): Promise<boolean> => {
      if (!onAutosave) return false;
      if (isOverCap(body)) {
        setSizeError(notebookNoteTooLarge());
        return false;
      }
      return onAutosave({ title: titleRef.current.trim() || "Untitled", content: body });
    },
    [onAutosave],
  );

  const { status: autosaveStatus, flush: flushAutosave } = useDebouncedAutosave({
    value: content,
    onSave: autosaveBody,
    // Read mode has nothing to persist, and a note that never resolved would
    // otherwise autosave an empty body over a body we simply failed to read.
    enabled: isEditing && Boolean(onAutosave) && !notFound && loadError === null,
  });

  async function handleDone() {
    if (isOverCap(content)) {
      setSizeError(notebookNoteTooLarge());
      return;
    }
    setSizeError(null);
    // Land any armed autosave first so the commit's rename moves a file whose
    // body is already current, then commit (which may rename on a new title).
    await flushAutosave();
    const ok = await onSave({ title: title.trim() || "Untitled", content });
    if (ok) {
      setIsEditing(false);
      setJustSaved(true);
    }
  }

  // ─── Not-found state (render-gate contract, §5) ──────────────────────
  if (notFound) {
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <ViewToolbar onBack={onBack} />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <FileWarning className="h-8 w-8 text-black/30 dark:text-white/30" strokeWidth={1.8} />
          <p className="text-sm font-bold text-black/70 dark:text-white/70">
            This note no longer exists.
          </p>
          <p className="max-w-xs text-xs text-black/45 dark:text-white/45">
            It may have been deleted or renamed. Pick another from the list.
          </p>
        </div>
      </div>
    );
  }

  // ─── Read-failure state (transport / oversize — NOT not-found, §5) ───
  if (loadError && !note) {
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <ViewToolbar onBack={onBack} />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <FileWarning className="h-8 w-8 text-black/30 dark:text-white/30" strokeWidth={1.8} />
          {/* Same measure for both lines — `loadError` is interpolated copy
              carrying the support-email suffix, so leaving it unconstrained
              ran it edge-to-edge over a max-w-xs subtext (route-level pane
              had the identical defect). */}
          <div className="flex max-w-md flex-col gap-2">
            <p className="text-sm font-bold text-black/70 dark:text-white/70">{loadError}</p>
            <p className="text-xs text-black/45 dark:text-white/45">
              The note is still there — it just couldn&apos;t be loaded right now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading state ───────────────────────────────────────────────────
  if (isLoading && !note) {
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <ViewToolbar onBack={onBack} />
        <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-sm text-black/40 dark:text-white/40">
          Loading note…
        </div>
      </div>
    );
  }

  const createdByAgent = note?.frontmatter?.createdBy === "agent";

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      {/* Document toolbar */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-black/5 px-4 py-3 dark:border-white/10">
        <div className="flex min-w-0 items-center gap-2">
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wide text-black/60 transition-colors hover:bg-black/5 md:hidden dark:text-white/60 dark:hover:bg-white/5"
            >
              ← Back
            </button>
          )}
          <span
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold",
              createdByAgent
                ? "bg-brand-coral/10 text-brand-coral-dark dark:text-brand-coral"
                : "bg-black/5 text-black/50 dark:bg-white/10 dark:text-white/50",
            )}
          >
            {createdByAgent ? (
              <Bot className="h-3 w-3" strokeWidth={2.2} />
            ) : (
              <PenLine className="h-3 w-3" strokeWidth={2.2} />
            )}
            {createdByAgent ? "written by agent" : "written by you"}
          </span>
          {onMove && (
            <div className="relative min-w-0">
              <button
                onClick={() => setMoveOpen((v) => !v)}
                title="Move to folder"
                className="flex min-w-0 items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold text-black/50 transition-colors hover:bg-black/10 dark:bg-white/10 dark:text-white/50 dark:hover:bg-white/15"
              >
                <Folder className="h-3 w-3 shrink-0" strokeWidth={2.2} />
                <span className="hidden truncate sm:inline">
                  {currentFolder ? currentFolder.split("/").join(" / ") : "Add to folder"}
                </span>
                <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.4} />
              </button>
              {moveOpen && (
                <>
                  <button
                    aria-label="Close folder picker"
                    className="fixed inset-0 z-20 cursor-default"
                    onClick={() => setMoveOpen(false)}
                  />
                  <div className="absolute left-0 top-7 z-30 max-h-64 w-56 overflow-y-auto rounded-xl border border-black/10 bg-white py-1 shadow-2xl dark:border-white/10 dark:bg-surface-dark-raised">
                    {currentFolder && (
                      <button
                        onClick={() => {
                          setMoveOpen(false);
                          onMove("");
                        }}
                        className="w-full px-3.5 py-2 text-left text-[13px] font-bold text-black/70 transition-colors hover:bg-black/5 dark:text-white/70 dark:hover:bg-white/5"
                      >
                        Notebook root
                      </button>
                    )}
                    {moveTargets.map((f) => (
                      <button
                        key={f}
                        onClick={() => {
                          setMoveOpen(false);
                          onMove(f);
                        }}
                        className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[13px] font-bold text-black/70 transition-colors hover:bg-black/5 dark:text-white/70 dark:hover:bg-white/5"
                      >
                        <Folder
                          className="h-3.5 w-3.5 shrink-0 text-black/40 dark:text-white/40"
                          strokeWidth={2.2}
                        />
                        <span className="truncate">{f.split("/").join(" / ")}</span>
                      </button>
                    ))}
                    {moveTargets.length === 0 && !currentFolder && (
                      <p className="px-3.5 py-2 text-xs text-black/45 dark:text-white/45">
                        No folders yet — create one in the sidebar.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <span className="min-w-0 truncate font-mono text-[11px] text-black/35 dark:text-white/35">
            {path}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {/* Full-screen reading. Lives HERE and nowhere else: the doc pane is
              the only one on screen in both states, so a control in the list
              would be unreachable exactly when it is needed to bring it back.
              Desktop-only — on mobile the list already yields on selection. */}
          {onToggleList && (
            <button
              onClick={onToggleList}
              aria-label={isListHidden ? "Show note list" : "Hide note list"}
              title={isListHidden ? "Show note list" : "Hide note list"}
              aria-pressed={isListHidden}
              className="hidden h-9 w-9 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/5 hover:text-black/70 md:flex dark:text-white/40 dark:hover:bg-white/5 dark:hover:text-white/70"
            >
              {isListHidden ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            onClick={onDelete}
            aria-label="Delete note"
            className="flex h-9 w-9 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/5 hover:text-black/70 dark:text-white/40 dark:hover:bg-white/5 dark:hover:text-white/70"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {isEditing ? (
            <>
              {/* Live autosave state. There is no Save button to press, so the
                  only honest way to tell someone their work is safe is to say
                  so continuously — and to say "Not saved" when it is not. */}
              <span
                role="status"
                aria-live="polite"
                className={cn(
                  "text-[11px] font-bold",
                  autosaveStatus === "error"
                    ? "text-red-700 dark:text-red-300"
                    : "text-black/45 dark:text-white/45",
                )}
              >
                {autosaveStatus === "saving"
                  ? "Saving…"
                  : autosaveStatus === "saved"
                    ? "Saved"
                    : autosaveStatus === "error"
                      ? "Not saved"
                      : autosaveStatus === "pending"
                        ? "Editing…"
                        : ""}
              </span>
              <button
                onClick={() => void handleDone()}
                disabled={isSaving}
                className="flex h-9 items-center gap-1.5 rounded-full bg-home-yellow px-4 text-[13px] font-bold text-home-ink transition-transform active:scale-95 disabled:opacity-60"
              >
                <Check className="h-4 w-4" strokeWidth={2.6} />
                {isSaving ? "Saving…" : "Done"}
              </button>
            </>
          ) : (
            <>
              {justSaved && (
                <span className="flex items-center gap-1 text-[11px] font-bold text-accent-green">
                  <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                  Saved
                </span>
              )}
              {/* Share only from read mode — publishing mid-edit would snapshot a draft.
                  The server reads the body from the vault (C2); only the path goes over. */}
              <ShareMenu path={path} updatedAt={updatedAt} />
              <button
                onClick={() => setIsEditing(true)}
                className="flex h-9 items-center gap-1.5 rounded-full border border-black/15 px-4 text-[13px] font-bold text-black/70 transition-colors hover:bg-black/5 dark:border-white/20 dark:text-white/70 dark:hover:bg-white/5"
              >
                <PenLine className="h-4 w-4" strokeWidth={2.4} />
                Edit
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save/delete failure — inline, honest, next to the action that failed. */}
      {(sizeError ?? actionError) && (
        <div className="shrink-0 border-b border-red-200 bg-red-50 px-4 py-2 text-xs font-bold text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {sizeError ?? actionError}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8">
        {/* Reading measure on laptops, widening to the full pane on very
            large screens (tables/images benefit; the editor prose is already
            max-w-none, so only this wrapper caps). */}
        <div className="mx-auto w-full max-w-2xl xl:max-w-4xl 2xl:max-w-none">
          {isEditing ? (
            <>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
                className="mb-4 w-full bg-transparent font-heading text-2xl font-bold text-black outline-none placeholder:text-black/30 dark:text-white dark:placeholder:text-white/30"
              />
              <Suspense
                fallback={
                  <div className="mt-6 text-sm text-black/40 dark:text-white/40">Loading editor…</div>
                }
              >
                <NotebookEditor value={content} onChange={setContent} autoFocus={!content} />
              </Suspense>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-black dark:text-white">
                {title || "Untitled"}
              </h1>
              {(note?.content ?? "").trim() ? (
                <div className="prose prose-base mt-4 max-w-none dark:prose-invert">
                  <MarkdownContent content={note?.content ?? ""} />
                </div>
              ) : (
                <p className="mt-4 text-black/40 dark:text-white/40">
                  Empty note — hit Edit to start writing.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Minimal toolbar shown in the loading / not-found states so Back stays reachable. */
function ViewToolbar({ onBack }: { onBack?: () => void }) {
  if (!onBack) return <div className="shrink-0 border-b border-black/5 px-4 py-3 dark:border-white/10" />;
  return (
    <div className="flex shrink-0 items-center border-b border-black/5 px-4 py-3 dark:border-white/10">
      <button
        onClick={onBack}
        className="rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wide text-black/60 transition-colors hover:bg-black/5 md:hidden dark:text-white/60 dark:hover:bg-white/5"
      >
        ← Back
      </button>
    </div>
  );
}
